/**
 * @file MediaPickerDialog.tsx
 * @description «Выбрать из медиатеки» (phase19.16 Р1; SB82; uiux-design-r1
 * §10, §11 риск 3; контракт §10 `GET /admin/content/media?type=image&q=`):
 * поиск по имени и подписи, плитки загруженных картинок, загрузка нового
 * файла с устройства. Общий для логотипов, значка вкладки, картинки для
 * ссылок, обложки ленты, задника, рисунка подвала и фото автора.
 *
 * `accept` — подходящие форматы места (логотип — только PNG и WebP, SB77):
 * неподходящие плитки видны, но выбрать их нельзя, и подпись объясняет
 * почему. Медиатека недоступна — остаётся загрузка с устройства.
 *
 * `cropAspect` — место, где кадр важен (фото автора 1:1, картинка для
 * ссылок 1200×630): новый файл сначала открывается в общем `ImageCropper`
 * редактора страниц. Кадрирование отдаёт JPEG, поэтому там, где нужна
 * прозрачность (логотипы, значок, задник, рисунок подвала), его нет.
 *
 * phase19.16 Р2: `mediaKind` — видео или звук для элементов секции
 * (SB97: только файлы медиатеки); список фильтруется по типу файла.
 */

import { Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";
import { SearchInput } from "../../ui/SearchInput";
import { Skeleton } from "../../ui/skeleton";
import { useNotify } from "../../ui/NotifyProvider";
import { ImageCropper } from "../../site-pages/ImageCropper";
import { MediaTile } from "./MediaTile";
import { listContentMedia, uploadContentMedia, type ContentMediaAsset } from "../../../lib/api-content-media";
import { toSiteMedia } from "../../../lib/site-view/media";
import type { SiteMedia } from "../../../lib/site-view/types";

interface Props {
  open: boolean;
  title: string;
  onClose: () => void;
  onSelect: (media: SiteMedia) => void;
  /** Подходящие MIME-типы; нет — любые изображения. */
  accept?: readonly string[];
  /** Подсказка о формате и размере: «PNG или WebP с прозрачным фоном…». */
  hint?: string;
  /** Подсказка кадра для кадрирования нового файла (ширина/высота). */
  cropAspect?: number;
  /** Что выбираем: картинку (по умолчанию), видео или звук. */
  mediaKind?: "image" | "video" | "audio";
}

/** Форматы загрузки по умолчанию для вида файла. */
const DEFAULT_ACCEPT: Readonly<Record<"image" | "video" | "audio", readonly string[]>> = {
  image: ["image/png", "image/webp", "image/jpeg"],
  video: ["video/mp4", "video/webm"],
  audio: ["audio/mpeg", "audio/mp4", "audio/ogg"],
};

const KIND_EMPTY: Readonly<Record<"image" | "video" | "audio", string>> = {
  image: "картинок",
  video: "видео",
  audio: "звуковых файлов",
};

export function MediaPickerDialog({ open, title, onClose, onSelect, accept, hint, cropAspect, mediaKind = "image" }: Props) {
  const notify = useNotify();
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<ContentMediaAsset[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(() => {
      setFailed(false);
      listContentMedia({ type: mediaKind === "image" ? "image" : undefined, q: query })
        .then((list) => !cancelled && setItems(list.filter((m) => m.mimeType.startsWith(`${mediaKind}/`))))
        .catch(() => !cancelled && setFailed(true));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, query, mediaKind]);

  const fits = (mime: string) => !accept || accept.includes(mime);

  // Кадрирование выдаёт JPEG — предлагаем его, только если JPEG здесь подходит.
  const canCrop = mediaKind === "image" && cropAspect !== undefined && fits("image/jpeg");

  async function upload(file: File) {
    setUploading(true);
    try {
      const asset = await uploadContentMedia(file);
      setPending(null);
      onSelect(toSiteMedia(asset));
    } catch (err) {
      notify.error(err instanceof Error ? err.message : "Не удалось загрузить файл.");
    } finally {
      setUploading(false);
    }
  }

  function onFile(file: File | undefined) {
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (!fits(file.type)) {
      notify.error(hint ?? "Этот формат здесь не подходит.");
      return;
    }
    if (canCrop) setPending(file);
    else void upload(file);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) return;
        setPending(null);
        onClose();
      }}
    >
      <DialogContent fullScreen className="flex max-h-[90vh] flex-col sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {hint ? <DialogDescription>{hint}</DialogDescription> : null}
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <SearchInput value={query} onChange={setQuery} placeholder="Найти по имени файла или подписи" ariaLabel="Поиск в медиатеке" className="min-w-0 flex-1" />
          <Button variant="outline" loading={uploading} onClick={() => fileRef.current?.click()}>
            <Upload aria-hidden />
            Загрузить с устройства
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept={(accept ?? DEFAULT_ACCEPT[mediaKind]).join(",")}
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            onChange={(e) => onFile(e.target.files?.[0])}
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {pending ? (
            <ImageCropper file={pending} busy={uploading} hintAspect={cropAspect} onCancel={() => setPending(null)} onDone={(file) => void upload(file)} />
          ) : failed ? (
            <p className="type-body text-on-surface-variant">Медиатека сейчас недоступна. Можно загрузить файл с устройства.</p>
          ) : items === null ? (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5" aria-busy="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="aspect-square rounded-lg" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="type-body text-on-surface-variant">
              {query ? `По запросу «${query}» ${KIND_EMPTY[mediaKind]} нет.` : `В медиатеке пока нет ${KIND_EMPTY[mediaKind]} — загрузите файл с устройства.`}
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {items.map((asset) => (
                <MediaTile key={asset.id} asset={asset} disabled={!fits(asset.mimeType)} onPick={() => onSelect(toSiteMedia(asset))} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
